import React, { useState, useContext, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import DialogTitle from "@mui/material/DialogTitle";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import sumBy from "lodash/sumBy";
import { useFormikContext } from "formik";
import { ItemInventoryMoveContext } from "../../MoveLocationsModal/ItemInventoryMove";
import StorageLocationAutocomplete from "../../../../views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import { formatRackLocationLabel } from "src/utils/rackLocations";

const buildDestination = (location, quantity) => ({
  ...location,
  warehouse_locations: [],
  sub_qty: quantity,
});

const styles = {
  quantityMode: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 999,
    overflow: "hidden",
    p: 0.5,
    "& .MuiToggleButton-root": {
      border: 0,
      borderRadius: 999,
      px: 3,
      py: 1,
      fontWeight: 500,
      textTransform: "none",
    },
    "& .Mui-selected": {
      bgcolor: "primary.light",
      color: "primary.main",
    },
  },
};

const getSourceLocationLabel = location =>
  formatRackLocationLabel(location, { fallback: "Current location" });

export default function InventoryMoveByItemIdAssistant({ projectId, onClose }) {
  const { values, setFieldValue } = useFormikContext();
  const { selectedRackLocation, autoSelectLocation } = useContext(ItemInventoryMoveContext);
  const rackLocation = values.locations?.[selectedRackLocation];
  const total = rackLocation?.sub_qty || 0;
  const sourceLabel = useMemo(() => getSourceLocationLabel(rackLocation), [rackLocation]);
  const [mode, setMode] = useState("entire");
  const [destinations, setDestinations] = useState(() => [buildDestination(rackLocation || {}, total)]);

  const visibleDestinations = useMemo(() => {
    if (mode === "entire") {
      const [firstDestination = buildDestination(rackLocation || {}, total)] = destinations;
      return [{ ...firstDestination, sub_qty: total }];
    }

    return destinations;
  }, [destinations, mode, rackLocation, total]);

  const allocatedQuantity = useMemo(
    () => sumBy(visibleDestinations, location => Number(location.sub_qty) || 0),
    [visibleDestinations]
  );
  const remainingQuantity = total - allocatedQuantity;
  const hasInvalidLocations = visibleDestinations.some(
    location => !location.warehouse_locations?.length || !(Number(location.sub_qty) > 0)
  );
  const canSubmit = mode === "entire"
    ? !hasInvalidLocations
    : allocatedQuantity > 0 && remainingQuantity >= 0 && !hasInvalidLocations;

  const handleModeChange = (_event, value) => {
    if (!value) {
      return;
    }

    setMode(value);
    setDestinations(currentDestinations => {
      const [firstDestination = buildDestination(rackLocation || {}, total)] = currentDestinations;

      if (value === "entire") {
        return [{ ...firstDestination, sub_qty: total }];
      }

      return currentDestinations.length
        ? currentDestinations.map(location => ({ ...location, sub_qty: "" }))
        : [buildDestination(rackLocation || {}, "")];
    });
  };

  const handleLocationChange = (index, locationData) => {
    setDestinations(currentDestinations => {
      const newDestinations = [...currentDestinations];
      newDestinations[index] = {
        ...newDestinations[index],
        ...locationData,
      };
      return newDestinations;
    });
  };

  const handleQuantityChange = (index, value) => {
    const nextQuantity = Number(value);

    if (Number.isNaN(nextQuantity) || nextQuantity < 0 || nextQuantity > total) {
      return;
    }

    setDestinations(currentDestinations => {
      const newDestinations = [...currentDestinations];
      newDestinations[index] = {
        ...newDestinations[index],
        sub_qty: nextQuantity,
      };
      return newDestinations;
    });
  };

  const handleAddDestination = () => {
    setDestinations(currentDestinations => [
      ...currentDestinations,
      buildDestination(rackLocation || {}, ""),
    ]);
  };

  const handleRemoveDestination = index => {
    setDestinations(currentDestinations => {
      if (currentDestinations.length === 1) {
        return currentDestinations;
      }

      return currentDestinations.filter((_, currentIndex) => currentIndex !== index);
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    const newLocations = [...values.locations];
    const existingLocation = newLocations[selectedRackLocation];
    const updatedDestinations = visibleDestinations.map(location => ({
      ...location,
      sub_qty: Number(location.sub_qty) || 0,
    }));
    const replacementLocations = remainingQuantity > 0
      ? [{ ...existingLocation, sub_qty: remainingQuantity }, ...updatedDestinations]
      : updatedDestinations;

    newLocations.splice(selectedRackLocation, 1, ...replacementLocations);
    setFieldValue("locations", newLocations);
    onClose();
  };

  useEffect(() => {
    if (autoSelectLocation) {
      const newLocations = [...values.locations];
      newLocations[selectedRackLocation] = {
        mobile_warehouse_location: autoSelectLocation.mobile_warehouse_location,
        sub_qty: autoSelectLocation.sub_qty,
        warehouse_locations: autoSelectLocation.warehouse_locations,
        warehouse: autoSelectLocation.warehouse,
        allocated_quantity: autoSelectLocation.allocated_quantity || 0,
      };
      setFieldValue("locations", newLocations);
      onClose();
    }
  }, [autoSelectLocation, onClose, selectedRackLocation, setFieldValue, values.locations]);

  useEffect(() => {
    if (!rackLocation) {
      return;
    }

    setMode("entire");
    setDestinations([buildDestination(rackLocation, total)]);
  }, [rackLocation, total]);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 600,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Typography
          align="center"
          color="textSecondary"
          sx={{ px: 2, fontWeight: 500, fontSize: 22 }}
        >
          Move Inventory
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Typography color="textPrimary" align="center">
              Moving From: {sourceLabel}, QTY: {total}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="center">
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={mode}
              onChange={handleModeChange}
              sx={styles.quantityMode}
            >
              <ToggleButton value="entire">Entire Quantity</ToggleButton>
              <ToggleButton value="split">Split Quantity</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            {visibleDestinations.map((location, index) => (
              <Box
                key={index}
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Box flex={1}>
                  <StorageLocationAutocomplete
                    index={index}
                    autoOpen={index === 0}
                    value={location}
                    projectId={projectId}
                    onChange={locationData => handleLocationChange(index, locationData)}
                    disabled={false}
                    loading={false}
                  />
                </Box>
                <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end">
                  <TextField
                    label="Location QTY"
                    type="number"
                    size="medium"
                    value={location.sub_qty || ""}
                    onChange={event => handleQuantityChange(index, event.target.value)}
                    inputProps={{ min: 0, max: total }}
                    sx={{ width: { xs: "100%", sm: 140 } }}
                    InputLabelProps={{ shrink: true }}
                    disabled={mode === "entire"}
                  />
                  {mode === "split" && visibleDestinations.length > 1 && (
                    <Tooltip title="Delete Location">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveDestination(index)}
                        sx={{ flexShrink: 0 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {mode === "split" && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={1}
              >
                <Button onClick={handleAddDestination}>Add Another Location</Button>
                <Typography
                  color="textSecondary"
                  sx={{
                    color: remainingQuantity < 0 ? "error.main" : "text.secondary",
                    textAlign: { xs: "center", sm: "right" },
                  }}
                >
                  Remaining in {sourceLabel}: {remainingQuantity}
                </Typography>
              </Box>
              <Typography
                color="textSecondary"
                variant="body2"
                sx={{ textAlign: { xs: "center", sm: "left" } }}
              >
                Any quantity you do not allocate will remain in {sourceLabel}.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
